/**
 * Hook para mantener el tab activo sincronizado con un query param de la URL.
 *
 * Patrón: `useSearchParams` de react-router-dom + validación contra la lista
 * de tabs válidas. Al F5 / share-link / back-button, el tab vuelve al estado
 * correcto en lugar de resetear al default.
 *
 * Uso:
 *   const [tab, setTab] = useUrlTab('tab', TABS, 'overview');
 *
 * Convenciones:
 *   - Cuando el tab activo es el default, se omite el param de la URL para
 *     mantenerla limpia.
 *   - El param se valida: si el user pega `?tab=foo` con un valor no listado,
 *     se ignora y queda el default.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useUrlTab<T extends string>(
  paramName: string,
  validTabs: ReadonlyArray<{ id: T }>,
  defaultTab: T,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(paramName);
  const valid = validTabs.some((t) => t.id === raw);
  const current = (valid ? (raw as T) : defaultTab);

  const set = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next === defaultTab) copy.delete(paramName);
          else copy.set(paramName, next);
          return copy;
        },
        { replace: true },
      );
    },
    [paramName, defaultTab, setParams],
  );

  return [current, set];
}

/**
 * Variante para valores arbitrarios (ej. el id de un span seleccionado).
 * No valida contra una lista — cualquier string entra. Cuando es `null` el
 * param se omite de la URL.
 *
 * Usa `replace: true` por default para no contaminar el history con cada
 * cambio (típico para selección dentro de una página).
 */
export function useUrlParam(
  paramName: string,
): [string | null, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const current = params.get(paramName);

  const set = useCallback(
    (next: string | null) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next === null || next === '') copy.delete(paramName);
          else copy.set(paramName, next);
          return copy;
        },
        { replace: true },
      );
    },
    [paramName, setParams],
  );

  return [current, set];
}

/**
 * Variante de {@link useUrlParam} que persiste la última preferencia del user
 * en `localStorage` bajo `storageKey`. La URL sigue siendo source-of-truth
 * cuando trae el param (compartir links sigue funcionando), pero si la URL
 * NO trae el param (navegación desde sidebar, F5 sin query) usamos la
 * preferencia guardada como fallback.
 *
 * Casos cubiertos:
 *   - Tildar filtro → URL `?p=1`, localStorage `1`.
 *   - F5 con URL `?p=1` → URL gana, localStorage no se toca.
 *   - Navegar a `/sessions` desde sidebar después de tildar antes → URL
 *     vacía, localStorage devuelve `1`, el filtro queda tildado y la URL
 *     se reescribe a `?p=1` en el primer render (efecto controlado).
 *   - Destildar → URL pierde el param, localStorage guarda `0` (explícito
 *     "off" para no caer a default al recargar).
 */
export function useUrlParamWithStorage(
  paramName: string,
  storageKey: string,
): [string | null, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const urlValue = params.get(paramName);

  // Fallback inicial: leer storage solo si la URL no trae nada. `null` =
  // sin preferencia guardada todavía → mantiene el default actual.
  let effective: string | null = urlValue;
  if (effective === null && typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(storageKey);
    // El usuario destildó explícitamente → respetar como "off" persistido.
    if (stored === '0' || stored === '' || stored === 'null') {
      effective = null;
    } else if (stored != null) {
      effective = stored;
    }
  }

  const set = useCallback(
    (next: string | null) => {
      if (typeof window !== 'undefined') {
        // Guardamos "0" explícito en lugar de removeItem para distinguir
        // "destildó antes" vs "nunca tildó" en el read inicial.
        window.localStorage.setItem(storageKey, next ?? '0');
      }
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next === null || next === '') copy.delete(paramName);
          else copy.set(paramName, next);
          return copy;
        },
        { replace: true },
      );
    },
    [paramName, storageKey, setParams],
  );

  return [effective, set];
}
